import Text from "common/Typography/Text.tsx";
import { BOTTOM_NAV_ITEM_ID, MOBILE_MENU_TRIGGER_ID } from "testIds";
import styled, { css, keyframes, useTheme } from "styled-components";
import useRoutesMatch from "utils/hooks/useRoutesMatch.ts";
import { useHistory } from "react-router-dom";
import { HEADER_ICON_SIZE } from "@shared/common/Themes/constants.ts";
import MenuOutlined from "@ant-design/icons/MenuOutlined";
import { Menu } from "lucide-react";
import LookIcon from "common/Icons/LookIcon.tsx";
import React from "react";
import { BOTTOM_NAVIGATOR_HEIGHT } from "common/constants.ts";
import { MenuItemConfig } from "types";
import Dropdown from "common/Dropdown/Dropdown.tsx";
import { BotType, LanguageNameSpaces } from "@shared/types";
import {
  useBottomNavigator,
  useIsHideBottomNavigatorPath,
  useVirtualKeyboard,
} from "utils/hooks/misc.tsx";
import { useTelegramWebAppBottomPadding } from "utils/hooks/useTelegramWebApp.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types";
import {
  useSelectUser,
  useSubUserPermissions,
} from "@shared/utils/hooks/misc.ts";
import useBottomNavigatorMenuItems from "common/Home/MobileHome/useBottomNavigatorMenuItems.tsx";
import {
  getBotItemKey,
  getBotsMenuItems,
} from "common/Home/Sider/MenuItems.tsx";
import { HEDGING_BOTS_FEATURE_FLAG } from "@shared/common/constants.ts";

interface BottomNavigatorProps {
  setMenuOpen: (open: boolean) => void;
}

const BottomNavigator = ({ setMenuOpen }: BottomNavigatorProps) => {
  const { menuItems } = useBottomNavigatorMenuItems();
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const theme = useTheme();
  const history = useHistory();
  const { shouldDisplay } = useBottomNavigator();
  const { isKeyboardOpen } = useVirtualKeyboard();
  const bottomPadding = useTelegramWebAppBottomPadding();
  const isHidePath = useIsHideBottomNavigatorPath();
  const { isReadWrite } = useSubUserPermissions();
  const user = useSelectUser();

  const height = bottomPadding + BOTTOM_NAVIGATOR_HEIGHT;

  const [selectedIndex] =
    useRoutesMatch(
      menuItems.map((item) => item.routerMatch),
      false
    ) || [];

  const textStyles = (active: boolean) =>
    theme.isNewLook
      ? undefined
      : {
          fontSize: 10,
          height: 15,
          color: theme.colors[active ? "primaryBlue" : "secondaryGrey"],
          paddingTop: 4,
        };

  const hasHedgingFeatureFlag = user?.hasFeatureFlag(HEDGING_BOTS_FEATURE_FLAG);
  const botsMenuItems = getBotsMenuItems(true, theme.isDarkMode, isReadWrite)
    .filter((item) => {
      return (
        hasHedgingFeatureFlag || item.key !== getBotItemKey(BotType.HEDGING)
      );
    })
    .map((item) => {
      return {
        key: item.key,
        label: t(item.name),
        onClick: () => history.push(item.link),
        icon: item.icon,
      };
    });

  const getMenuItem = (item: MenuItemConfig, index: number) => {
    const selected = index === selectedIndex;
    return (
      <ItemWrapper
        key={item.key}
        data-testid={BOTTOM_NAV_ITEM_ID}
        aria-current={selected ? "page" : undefined}
        onClick={() => item.link && history.push(item.link)}
      >
        <IconWrapper $selected={selected}>{item.icon}</IconWrapper>
        <NavLabel $active={selected} style={textStyles(selected)}>
          {t(item.name as TranslationKeys<LanguageNameSpaces.COMMON>)}
        </NavLabel>
      </ItemWrapper>
    );
  };

  const getBotsMenuItem = (item: MenuItemConfig, index: number) => {
    if (user?.isWhopExperience()) {
      return getMenuItem(item, index);
    }
    const selected = index === selectedIndex;
    return (
      <Dropdown
        title="Bot Type"
        menu={{ items: botsMenuItems }}
        trigger={["click"]}
        placement="top"
        key={item.key}
      >
        <ItemWrapper
          data-testid={BOTTOM_NAV_ITEM_ID}
          aria-current={selected ? "page" : undefined}
        >
          <IconWrapper $selected={selected}>{item.icon}</IconWrapper>
          <NavLabel $active={selected} style={textStyles(selected)}>
            {t(item.name as TranslationKeys<LanguageNameSpaces.COMMON>)}
          </NavLabel>
        </ItemWrapper>
      </Dropdown>
    );
  };

  return (
    <Wrapper
      $height={height}
      $hide={!shouldDisplay || isKeyboardOpen || isHidePath}
    >
      {menuItems.map((item, index) => {
        if (item.key === "bots") {
          return getBotsMenuItem(item, index);
        } else {
          return getMenuItem(item, index);
        }
      })}
      <ItemWrapper
        data-testid={MOBILE_MENU_TRIGGER_ID}
        onClick={() => setMenuOpen(true)}
      >
        <IconWrapper>
          <LookIcon
            classic={
              <MenuOutlined
                style={{
                  fontSize: HEADER_ICON_SIZE,
                  marginTop: "auto",
                  marginBottom: "auto",
                }}
              />
            }
            newLook={<Menu strokeWidth={1.75} />}
          />
        </IconWrapper>
        <NavLabel $active={false} style={textStyles(false)}>
          {t("page_title.menu")}
        </NavLabel>
      </ItemWrapper>
    </Wrapper>
  );
};

export default React.memo(BottomNavigator);

const NAV_TRANSITION = "220ms cubic-bezier(0.2, 0, 0, 1)";
const navPop = keyframes`
  0% {
    transform: scale(0.86);
  }
  60% {
    transform: scale(1.08);
  }
  100% {
    transform: scale(1);
  }
`;
const NARROW_PHONE_MAX_WIDTH = "399.98px";

const Wrapper = styled.div<{ $height: number; $hide?: boolean }>`
  width: 100%;
  display: flex;
  align-items: flex-start;
  padding-top: ${(p) => (p.theme.isNewLook ? 6 : 8)}px;
  background-color: ${(p) =>
    p.theme.isNewLook ? p.theme.colors.bgColor : p.theme.colors.layoutBgColor};
  border-top: 1px solid ${(p) => p.theme.colors.lightestBorderGrey};
  height: ${(p) => p.$height}px;
  z-index: 1000;
  ${(p) =>
    p.$hide &&
    `
      display: none;
    `};
  ${(p) =>
    !p.$height &&
    `
    padding-top: 0;
  `};
`;
const ItemWrapper = styled.div`
  flex: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
const IconWrapper = styled.div<{ $selected?: boolean }>`
  width: 22px;
  height: 22px;
  color: ${(p) =>
    p.theme.colors[p.$selected ? "primaryBlue" : "secondaryGrey"]};
  svg {
    color: ${(p) =>
      p.theme.colors[p.$selected ? "primaryBlue" : "secondaryGrey"]};
    width: 22px;
    height: 22px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      display: flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 28px;
      svg {
        width: 20px;
        height: 20px;
        stroke-width: 1.75;
        transition: color ${NAV_TRANSITION};
        ${p.$selected &&
        css`
          animation: ${navPop} 0.28s ease-out;
        `}
      }
      @media (prefers-reduced-motion: reduce) {
        svg {
          animation: none;
        }
      }
    `}
`;
const NavLabel = styled(Text)<{ $active: boolean }>`
  ${(p) =>
    p.theme.isNewLook &&
    css`
      padding-top: 2px;
      font-size: 11px;
      line-height: 14px;
      font-weight: ${p.theme.typography.fontWeights.MEDIUM};
      white-space: nowrap;
      color: ${p.theme.colors[p.$active ? "primaryBlue" : "secondaryGrey"]};
      transition: color ${NAV_TRANSITION};
      @media (max-width: ${NARROW_PHONE_MAX_WIDTH}) {
        font-size: 10px;
        letter-spacing: -0.01em;
      }
    `}
`;
